import React, { useRef, useState } from 'react';
import { MenuItem, Box, TextField, IconButton, Button } from '@mui/material';
import PopperSelect from '../../shared/components/PopperSelect';
import { Add, Edit, Delete, Check, Clear } from '@mui/icons-material';
import { COLORS, isValidHex } from '../../shared/colors';
import { useTranslation } from 'react-i18next';
import CustomColorPopover from '../shared/CustomColorPopover';
import CommonDeleteConfirmDialog from '../shared/CommonDeleteConfirmDialog';

const ManagedFilterSelect = ({
  value,
  onChange,
  multiple = false,
  label,
  renderValue,
  menuPlacement = 'bottom',
  items = [],
  allOptionLabel,
  allOptionValue = 'all',
  unresolvedOptionLabel = '',
  menuPaperStyle,
  menuPaperClassName,
  sx,
  displayEmpty = true,
  allowCreate = false,
  canManage = false,
  createText,
  createPlaceholder,
  maxLength = 75,
  withColor = false,
  onCreate,
  onUpdate,
  onDelete,
  onClose,
  getItemId = (item) => item.id,
  getItemName = (item) => item.name,
  getItemColor = (item) => item.colour || COLORS.primary,
  isItemInactive = (item) => !!item.status,
  isItemSelected = () => false,
  renderItemLeft,
  deferChanges = false
}) => {
  const { t } = useTranslation();
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(COLORS.primary);
  const [editingItem, setEditingItem] = useState(null);
  const createLockRef = useRef(false);
  const [isCreatingSubmitting, setIsCreatingSubmitting] = useState(false);
  const deleteLockRef = useRef(false);
  const [isDeletingSubmitting, setIsDeletingSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const renderSelectedText = (text) => (
    <Box component="span" className="filter-selected-value-text">
      {text}
    </Box>
  );
  const [createPickerAnchor, setCreatePickerAnchor] = useState(null);
  const [editPickerAnchor, setEditPickerAnchor] = useState(null);
  const [draftValue, setDraftValue] = useState(value);
  const [closeSignal, setCloseSignal] = useState(0);

  React.useEffect(() => {
    setDraftValue(value);
  }, [value]);

  const handleSelectionChange = (event) => {
    let nextValue = event.target.value;
    if (deferChanges && multiple) {
      nextValue = Array.isArray(nextValue) ? nextValue : [nextValue];
      if (nextValue.some((entry) => String(entry) === String(allOptionValue))) {
        const allWasAlreadySelected = Array.isArray(draftValue) && draftValue.some((entry) => String(entry) === String(allOptionValue));
        nextValue = allWasAlreadySelected
          ? nextValue.filter((entry) => String(entry) !== String(allOptionValue))
          : [allOptionValue];
      }
      setDraftValue(nextValue);
      return;
    }
    onChange?.(event);
  };

  const handleCancelChanges = () => {
    setDraftValue(value);
    setCloseSignal((signal) => signal + 1);
  };

  const handleApplyChanges = () => {
    onChange?.({ target: { value: draftValue } });
    setCloseSignal((signal) => signal + 1);
  };


  const defaultRenderValue = (selected) => {
    if (multiple) {
      const selectedValues = Array.isArray(selected) ? selected : [];
      if (selectedValues?.includes?.(allOptionValue) || selectedValues?.length === 0) {
        return renderSelectedText(allOptionLabel);
      }

      const names = selectedValues
        ?.map?.(selectedId => items?.find?.(item => String(getItemId(item)) === String(selectedId)))
        ?.filter?.(Boolean)
        ?.map?.(getItemName) || [];

      return renderSelectedText(names?.length > 0 ? names.join(', ') : unresolvedOptionLabel);
    }

    if (!selected || selected === allOptionValue) {
      return renderSelectedText(allOptionLabel);
    }

    const item = items?.find?.(option => String(getItemId(option)) === String(selected));
    return renderSelectedText(item ? getItemName(item) : unresolvedOptionLabel);
  };

  const resetLocalState = () => {
    setIsCreating(false);
    setNewName('');
    setNewColor(COLORS.primary);
    setEditingItem(null);
  };

  const handleCreate = async () => {
    if (!newName.trim() || !onCreate || createLockRef.current || (withColor && !isValidHex(newColor))) return;

    createLockRef.current = true;
    setIsCreatingSubmitting(true);

    try {
      await onCreate({ name: newName.trim(), colour: newColor });
      resetLocalState();
    } catch (e) {
      createLockRef.current = false;
      setIsCreatingSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingItem?.newName?.trim() || !onUpdate || (withColor && !isValidHex(editingItem.newColor))) return;
    await onUpdate({
      id: editingItem.id,
      name: editingItem.newName.trim(),
      colour: editingItem.newColor
    });
    setEditingItem(null);
  };

  const handleDelete = async (item) => {
    if (deleteLockRef.current || !onDelete) return;

    deleteLockRef.current = true;
    setIsDeletingSubmitting(true);

    try {
      await onDelete(item);
    } catch (error) {
      throw error;
    } finally {
      deleteLockRef.current = false;
      setIsDeletingSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      await handleDelete(itemToDelete);
    } catch (err) {
      // Handle error if needed
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const getDeleteTitle = () => {
    if (!label) return t('common.delete');
    const lower = label.toLowerCase();
    if (lower.includes('label')) return t('taskModal.deleteLabel');
    if (lower.includes('sprint')) return t('taskModal.deleteSprint');
    if (lower.includes('type')) return t('taskModal.deleteTaskType');
    if (lower.includes('priority')) return t('taskModal.deletePriority');
    return `${t('common.delete')} ${label}`;
  };

  const getDeleteQuestion = () => {
    if (!label) return t('common.deleteConfirmation');
    const lower = label.toLowerCase();
    if (lower.includes('label')) return t('taskModal.confirmDeleteLabel');
    if (lower.includes('sprint')) return t('taskModal.confirmDeleteSprint');
    if (lower.includes('type')) return t('taskModal.confirmDeleteTaskType');
    if (lower.includes('priority')) return t('taskModal.confirmDeletePriority');
    return t('common.deleteConfirmationItem', { item: label });
  };

  return (
    <>
      <PopperSelect
        multiple={multiple}
        value={deferChanges ? draftValue : value}
        displayEmpty={displayEmpty}
        onChange={handleSelectionChange}
        closeSignal={deferChanges ? closeSignal : undefined}
        footer={deferChanges ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 1.25, py: 0.75, borderTop: `1px solid ${COLORS.gray[200]}`, bgcolor: COLORS.white, flexShrink: 0 }}>
            <Button size="small" variant="outlined" onClick={handleCancelChanges}>{t('common.cancel', 'Cancel')}</Button>
            <Button size="small" variant="contained" onClick={handleApplyChanges}>{t('common.apply', 'Apply')}</Button>
          </Box>
        ) : undefined}
        onClose={() => {
          resetLocalState();
          if (deferChanges) setDraftValue(value);
          if (onClose) onClose();
        }}
        label={label}
        renderValue={renderValue || defaultRenderValue}
        MenuProps={{
          anchorOrigin: {
            vertical: menuPlacement === 'top' ? 'top' : 'bottom',
            horizontal: 'left'
          },
          transformOrigin: {
            vertical: menuPlacement === 'top' ? 'bottom' : 'top',
            horizontal: 'left'
          },
          PaperProps: {
            className: menuPaperClassName,
            style: menuPaperStyle || {
              maxHeight: 200,
              maxWidth: 300,
              overflow: 'auto'
            },
            onMouseDown: (e) => {
              if (e.target.closest('.create-option')) {
                e.preventDefault();
              }
            }
          }
        }}
        sx={sx}
      >
        {allowCreate && !isCreating && (
          <MenuItem
            className="create-option"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setEditingItem(null);
              setIsCreating(true);
            }}
            sx={{ borderBottom: `1px solid ${COLORS.gray[200]}` }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: COLORS.primary }}>
              <Add sx={{ fontSize: 16 }} />
              <span>{createText}</span>
            </Box>
          </MenuItem>
        )}

        {allowCreate && isCreating && (
          <MenuItem className="create-option" sx={{ borderBottom: `1px solid ${COLORS.gray[200]}` }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%' }}>
              <TextField
                size="small"
                placeholder={createPlaceholder}
                value={newName}
                onChange={(e) => {
                  e.stopPropagation();
                  setNewName(e.target.value);
                }}
                inputProps={{ maxLength }}
                helperText={`${newName.length}/${maxLength} characters`}
                sx={{ flex: 1 }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreate();
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    resetLocalState();
                  }
                }}
                onMouseDown={(e) => e.stopPropagation()}
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
              {withColor && (
                  <>
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        setCreatePickerAnchor(e.currentTarget);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '4px',
                        cursor: 'pointer',
                        border: `1px solid ${COLORS.gray[400]}`,
                        bgcolor: isValidHex(newColor) ? newColor : '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 'inset 0 0 4px rgba(0,0,0,0.15)',
                        '&:hover': { transform: 'scale(1.1)' }
                      }}
                    />
                    <CustomColorPopover
                      open={Boolean(createPickerAnchor)}
                      anchorEl={createPickerAnchor}
                      onClose={() => setCreatePickerAnchor(null)}
                      colour={newColor}
                      onChange={setNewColor}
                    />
                  </>
                )}
                <IconButton
                  size="small"
                  aria-label="confirm-create"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreate();
                  }}
                  sx={{ color: COLORS.success, mt: 0.25 }}
                  disabled={isCreatingSubmitting || !newName.trim() || newName.length > maxLength || (withColor && !isValidHex(newColor))}
                >
                  <Check sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="cancel-create"
                  onClick={(e) => {
                    e.stopPropagation();
                    resetLocalState();
                  }}
                  sx={{ color: COLORS.error, mt: 0.25 }}
                >
                  <Clear sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>
          </MenuItem>
        )}

        <MenuItem value={allOptionValue}>{allOptionLabel}</MenuItem>

        {items?.map?.((item) => {
          const id = getItemId(item);
          const name = getItemName(item);
          const color = getItemColor(item);
          const selected = deferChanges && multiple
            ? (Array.isArray(draftValue) && draftValue.some((entry) => String(entry) === String(id)))
            : isItemSelected(item);
          const inactive = isItemInactive(item);

          return (
            <MenuItem key={id} value={editingItem?.id === id ? '' : id}>
              {editingItem?.id === id ? (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%' }}>
                  <TextField
                    size="small"
                    value={editingItem.newName}
                    onChange={(e) => {
                      e.stopPropagation();
                      setEditingItem(prev => ({ ...prev, newName: e.target.value }));
                    }}
                    inputProps={{ maxLength }}
                    helperText={`${editingItem.newName.length}/${maxLength} characters`}
                    sx={{ flex: 1 }}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleUpdate();
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        setEditingItem(null);
                      }
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                  />
                  {withColor && (
                      <>
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditPickerAnchor(e.currentTarget);
                          }}
                      onMouseDown={(e) => e.stopPropagation()}
                      sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '4px',
                            cursor: 'pointer',
                            border: `1px solid ${COLORS.gray[400]}`,
                            bgcolor: isValidHex(editingItem.newColor) ? editingItem.newColor : '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 'inset 0 0 4px rgba(0,0,0,0.15)',
                            '&:hover': { transform: 'scale(1.1)' }
                          }}
                        />
                        <CustomColorPopover
                          open={Boolean(editPickerAnchor)}
                          anchorEl={editPickerAnchor}
                          onClose={() => setEditPickerAnchor(null)}
                          colour={editingItem.newColor}
                          onChange={(val) => setEditingItem(prev => ({ ...prev, newColor: val }))}
                        />
                      </>
                    )}
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUpdate(); }} disabled={!editingItem.newName.trim() || editingItem.newName.length > maxLength || (withColor && !isValidHex(editingItem.newColor))} sx={{ color: COLORS.success, mt: 0.25 }}>
                      <Check sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingItem(null); }} sx={{ color: COLORS.error, mt: 0.25 }}>
                      <Clear sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {renderItemLeft(item, { id, name, color })}
                  </Box>
                  {!selected && !inactive && canManage && (
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsCreating(false);
                          setNewName('');
                          setEditingItem({ ...item, id, newName: name, newColor: color });
                        }}
                        sx={{ p: 0.25 }}
                      >
                        <Edit sx={{ fontSize: 14 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="delete-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setItemToDelete(item);
                          setDeleteDialogOpen(true);
                        }}
                        sx={{ p: 0.25 }}
                        disabled={isDeletingSubmitting}
                      >
                        <Delete sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              )}
            </MenuItem>
          );
        })}
      </PopperSelect>
      <CommonDeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title={getDeleteTitle()}
        question={getDeleteQuestion()}
        itemName={itemToDelete ? getItemName(itemToDelete) : ''}
        warning={t('taskModal.actionCannotBeUndone')}
        confirmText={getDeleteTitle()}
        cancelText={t('common.cancel')}
      />
    </>
  );
};

export default ManagedFilterSelect;
